import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import {
  GuiaBreadcrumb,
  ConversionBlock,
  PriceTag,
  GuiaFAQ,
  GuiaRelacionadas,
  SchemaArticle,
  SchemaFAQPage,
} from '../components/GuiaComponents';
import ReassuranceSection from '@/components/feature/ReassuranceSection';

const FAQS = [
  {
    question: '¿Se puede reparar un brazo de toldo roto o hay que cambiarlo entero?',
    answer:
      'Depende del tipo de rotura. Si es un problema en el muelle o en el engranaje interno, a menudo se puede reparar sustituyendo solo esa pieza. Si el perfil de aluminio está doblado o partido, lo más recomendable es cambiar el brazo completo para garantizar la seguridad y el correcto funcionamiento del toldo.',
  },
  {
    question: '¿Cuánto cuesta reparar un brazo de toldo en Málaga?',
    answer:
      'La reparación de un muelle o engranaje interno ronda los 70-100 € por brazo. Si hay que sustituir el brazo completo, el precio oscila entre 90 € (brazo simple de punto recto) y 180 € (brazo articulado de doble sección). El precio final depende del modelo y dimensiones del toldo.',
  },
  {
    question: '¿El viento de levante puede romper un brazo de toldo?',
    answer:
      'Totalmente. El viento de levante en Málaga puede alcanzar rachas de más de 60 km/h, y si el toldo está extendido sin protección, la presión del viento sobre la lona se transmite directamente a los brazos. Es una de las causas más frecuentes de brazos doblados o partidos en la costa malagueña. Recomendamos instalar un sensor de viento si tu toldo es eléctrico.',
  },
  {
    question: '¿Los brazos de punto recto y los articulados se reparan igual?',
    answer:
      'No exactamente. Los brazos de punto recto son más sencillos y suelen tener un único muelle y menos piezas móviles. Los brazos articulados tienen secciones que se pliegan y varios puntos de tensión, lo que los hace más complejos de reparar. Ambos requieren herramientas específicas y conocimiento del mecanismo para manipular los muelles con seguridad.',
  },
];

export default function GuiaBrazoToldoRoto() {
  return (
    <>
      <title>Brazo de toldo roto: reparación | Toldos Málaga</title>
      <meta
        name="description"
        content="¿Brazo de toldo roto o doblado? Reparación profesional de brazos de punto recto y articulados en Málaga. Reparamos muelles, engranajes y brazos completos. Presupuesto gratis."
      />
      <meta
        name="keywords"
        content="brazo toldo roto, reparar brazo toldo punto recto, reparar muelle brazo toldo, reparar engranaje toldo, brazo toldo malaga"
      />
      <link rel="canonical" href="https://toldos-malaga.es/guias/brazo-toldo-roto" />
      <SchemaArticle
        headline="Brazo de toldo roto: reparación profesional en Málaga"
        description="Guía sobre la reparación de brazos de toldo rotos, doblados o con muelles dañados. Tipos de brazos, causas de rotura, precios orientativos y consejos de seguridad."
        datePublished="2025-11-21"
        dateModified="2026-06-16"
        imageUrl="/images/013-guia-brazo-roto-og.webp"
        canonicalUrl="https://toldos-malaga.es/guias/brazo-toldo-roto"
      />
      <SchemaFAQPage faqs={FAQS} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Reparación de brazo de toldo roto en Málaga',
              description: 'Reparación profesional de brazos de toldo de punto recto y articulados. Sustitución de muelles, engranajes y brazos completos en Málaga.',
            },
            priceSpecification: {
              '@type': 'PriceSpecification',
              price: 90,
              priceCurrency: 'EUR',
            },
          }),
        }}
      />
      <Navbar />
      <main>
        <GuiaBreadcrumb currentTitle="Brazo de toldo roto: reparación" />

        <section className="w-full max-w-[1280px] mx-auto px-4 md:px-6 pt-6 pb-10">
          <div className="relative rounded-2xl overflow-hidden h-[280px] md:h-[380px]">
            <img
              src="/images/012-guia-brazo-roto-hero.webp"
              alt="Brazo de toldo roto en una fachada de Málaga"
              title="Brazo de toldo dañado - Reparación profesional Málaga"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/15 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
              <span className="inline-block px-3 py-1 rounded-full bg-accent-500/90 text-foreground-900 text-xs font-semibold mb-3">
                Guía de reparación
              </span>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-white">
                Brazo de toldo roto: reparación
              </h1>
            </div>
          </div>
        </section>

        <ReassuranceSection />

        <article className="w-full max-w-4xl mx-auto px-4 md:px-6 pb-14">
          <div className="prose-custom space-y-6 text-foreground-700 leading-relaxed">

            <p className="text-lg text-foreground-600">
              Un brazo de toldo roto no es solo un problema estético: es un riesgo de seguridad. Un brazo dañado puede ceder en cualquier momento, provocando la caída completa del toldo sobre personas o mobiliario. En Málaga, donde el viento de levante y el salitre castigan las estructuras metálicas día tras día, esta avería es desgraciadamente frecuente. Te contamos qué tipos de rotura existen y cómo se reparan.
            </p>

            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10">
              Tipos de brazos de toldo y cómo se rompen
            </h2>

            <p>
              Antes de entrar en reparaciones, conviene entender que no todos los brazos de toldo son iguales. Los dos tipos principales son:
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-5 rounded-xl bg-secondary-100/70 border border-secondary-300/60">
                <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center mb-3">
                  <i className="ri-ruler-line text-primary-600"></i>
                </div>
                <h3 className="font-heading font-bold text-foreground-900 text-base mb-2">
                  Brazo de punto recto
                </h3>
                <p className="text-sm text-foreground-600">
                  El más común en toldos pequeños y medianos. Una sola barra rígida que se extiende en línea recta. Suele romperse por fatiga del aluminio en el punto de anclaje o por torsión durante un golpe de viento.
                </p>
              </div>
              <div className="p-5 rounded-xl bg-secondary-100/70 border border-secondary-300/60">
                <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center mb-3">
                  <i className="ri-links-line text-primary-600"></i>
                </div>
                <h3 className="font-heading font-bold text-foreground-900 text-base mb-2">
                  Brazo articulado
                </h3>
                <p className="text-sm text-foreground-600">
                  Compuesto por dos o más secciones unidas por codos. Ofrece mayor proyección. Sus puntos débiles son las articulaciones, los muelles internos y los pasadores que unen las secciones.
                </p>
              </div>
            </div>

            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10">
              Causas principales de rotura en Málaga
            </h2>

            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-primary-600 text-xs font-bold">1</span>
                </div>
                <div>
                  <strong className="text-foreground-900">Viento de levante.</strong>
                  <p className="text-sm text-foreground-600 mt-1">
                    Rachas fuertes que golpean la lona y transmiten una presión brutal a los brazos. Un toldo extendido actúa como una vela: en segundos, un brazo puede doblarse como un papel. En la costa de Málaga, este es el motivo número uno de sustitución de brazos.
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-primary-600 text-xs font-bold">2</span>
                </div>
                <div>
                  <strong className="text-foreground-900">Corrosión por salitre.</strong>
                  <p className="text-sm text-foreground-600 mt-1">
                    La brisa marina transporta partículas de sal que se depositan en los brazos metálicos. Con el tiempo, el aluminio se pica, los muelles de acero se oxidan y los puntos de fijación pierden resistencia. Un brazo corroído puede partirse incluso con un uso normal.
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-primary-600 text-xs font-bold">3</span>
                </div>
                <div>
                  <strong className="text-foreground-900">Fatiga del material.</strong>
                  <p className="text-sm text-foreground-600 mt-1">
                    Abrir y cerrar el toldo cientos de veces al año durante varias temporadas genera microfisuras en el aluminio que, un día, ceden. Especialmente en toldos con más de 8-10 años de antigüedad.
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-primary-600 text-xs font-bold">4</span>
                </div>
                <div>
                  <strong className="text-foreground-900">Golpe accidental.</strong>
                  <p className="text-sm text-foreground-600 mt-1">
                    Un portazo de la persiana metálica al bajar, un golpe al mover muebles de terraza, o el impacto de una rama durante un día de poniente. A veces, el daño es evidente; otras veces, una pequeña abolladura se convierte en una rotura semanas después.
                  </p>
                </div>
              </div>
            </div>

            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10">
              ¿Puedo cambiar yo el brazo del toldo?
            </h2>

            <p>
              Cambiar un brazo de toldo no es como cambiar una bombilla. El brazo está sometido a la tensión de un muelle interno que, si se suelta sin control, puede causar lesiones serias en manos y cara. Además, el brazo va fijado a la pared y al perfil del toldo con tornillería específica que requiere llaves dinamométricas para no dañar la fachada.
            </p>

            <p>
              Si eres muy manitas y tienes las herramientas adecuadas, podrías plantearte cambiar un brazo sencillo de punto recto. Pero te advertimos: en cuanto abras la tapa del mecanismo, te enfrentas a un muelle en tensión que no perdona errores. Y en brazos articulados, la complejidad se multiplica: hay que desmontar varias secciones, sincronizar los codos y ajustar la tensión de cada tramo.
            </p>

            <div className="p-4 rounded-xl bg-[#FEF3C7] border border-yellow-300/60">
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-yellow-400/80 flex items-center justify-center flex-shrink-0">
                  <i className="ri-alert-line text-foreground-900 text-base"></i>
                </div>
                <div>
                  <strong className="text-foreground-900 text-sm">Atención:</strong>
                  <p className="text-sm text-foreground-700 mt-1">
                    Nunca intentes reparar un brazo con el toldo parcialmente extendido y sin haber liberado la tensión del muelle. Si el brazo está visiblemente doblado o partido, no forces el toldo para cerrarlo: podrías dañar también la lona y el eje de enrollado.
                  </p>
                </div>
              </div>
            </div>

            <div className="my-8">
              <ConversionBlock
                title="¿Brazo roto en Málaga? Lo reparamos hoy mismo"
                subtitle="Reparamos brazos de punto recto y articulados de todas las marcas. Desplazamiento a toda la provincia en 24/48h. Presupuesto gratis sin compromiso."
              />
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 my-8">
              <PriceTag price="desde 90 €" description="Reparación brazo simple" />
              <PriceTag price="desde 150 €" description="Brazo articulado completo" />
              <p className="text-sm text-foreground-500">
                Precios orientativos. El coste exacto depende del tipo de brazo y la complejidad del montaje.
              </p>
            </div>

            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10">
              Preguntas frecuentes
            </h2>

            <GuiaFAQ faqs={FAQS} />

            <div className="mt-12">
              <GuiaRelacionadas />
            </div>

          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}